import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, Flame, Trophy, Target } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { format } from 'date-fns';

export function ProgressTracker() {
  const { user } = useAuth();
  const [engagement, setEngagement] = useState<any>(null);
  const [achievements, setAchievements] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEngagement();
    loadAchievements();
  }, [user]);

  const loadEngagement = async () => {
    if (!user?.id) return;
    try {
      let { data, error } = await supabase
        .from('user_engagement')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      // Create if doesn't exist
      if (!data) {
        const { data: newData, error: insertError } = await supabase
          .from('user_engagement')
          .insert({ user_id: user.id })
          .select()
          .single();
        
        if (insertError) throw insertError;
        data = newData;
      }

      if (error) throw error;
      setEngagement(data);
    } catch (error) {
      console.error('Error loading engagement:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAchievements = async () => {
    if (!user?.id) return;
    try {
      const { data: achievementsData, error } = await supabase
        .from('user_achievements')
        .select('*')
        .eq('user_id', user.id)
        .order('earned_at', { ascending: false })
        .limit(5);

      if (error) throw error;

      // Fetch milestone details separately
      const milestoneIds = achievementsData?.map(a => a.milestone_id) || [];
      const { data: milestonesData } = await supabase
        .from('achievement_milestones')
        .select('*')
        .in('id', milestoneIds);

      const milestoneMap = new Map(milestonesData?.map(m => [m.id, m]));
      const enriched = achievementsData?.map(a => ({
        ...a,
        achievement_milestones: milestoneMap.get(a.milestone_id)
      })) || [];

      setAchievements(enriched);
    } catch (error) {
      console.error('Error loading achievements:', error);
    }
  };

  if (loading) return <div>Loading...</div>;

  const totalGoal = 50;
  const completionPercent = ((engagement?.courses_completed || 0) / totalGoal) * 100;

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Your Progress
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Courses Completed</span>
              <span className="text-sm text-muted-foreground">
                {engagement?.courses_completed || 0} / {totalGoal}
              </span>
            </div>
            <Progress value={completionPercent} />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-3 bg-muted rounded-lg">
              <div className="text-2xl font-bold">{engagement?.total_pd_hours || 0}</div>
              <div className="text-xs text-muted-foreground">PD Hours</div>
            </div>
            <div className="text-center p-3 bg-muted rounded-lg">
              <div className="text-2xl font-bold">{engagement?.videos_watched || 0}</div>
              <div className="text-xs text-muted-foreground">Videos</div>
            </div>
            <div className="text-center p-3 bg-muted rounded-lg">
              <div className="text-2xl font-bold">{engagement?.total_points || 0}</div>
              <div className="text-xs text-muted-foreground">Points</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Flame className="h-5 w-5" />
            Engagement Streak
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-center p-4 bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-lg border border-orange-200">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Flame className="h-8 w-8 text-orange-500" />
              <div className="text-4xl font-bold">{engagement?.current_streak_days || 0}</div>
            </div>
            <div className="text-sm text-muted-foreground">Day Streak</div>
            <div className="text-xs text-muted-foreground mt-1">
              Longest: {engagement?.longest_streak_days || 0} days
            </div>
          </div>

          {achievements.length > 0 && (
            <div>
              <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                <Trophy className="h-4 w-4" />
                Recent Milestones
              </h4>
              <div className="space-y-2">
                {achievements.slice(0, 3).map((achievement) => (
                  <div key={achievement.id} className="flex items-center gap-2 p-2 bg-muted rounded">
                    <span className="text-lg">{achievement.achievement_milestones?.icon || '🏆'}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        {achievement.achievement_milestones?.name}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {format(new Date(achievement.earned_at), 'PP')}
                      </div>
                    </div>
                    <Badge variant="outline">+{achievement.achievement_milestones?.points || 0}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}